/*
  Esqueletos de carregamento.

  A regra que organiza este arquivo: **o esqueleto tem a forma do conteúdo
  que vem**. Um retângulo cinza genérico no lugar de uma página inteira
  cumpre o mínimo (dizer "está carregando") e erra o resto — quando os dados
  chegam, o layout salta, porque o que estava ali não tinha a mesma altura
  nem a mesma estrutura. Esqueleto que não reserva o espaço certo é só um
  spinner mais caro.

  Por isso as peças aqui são por FORMA (faixa de indicadores, tabela, gráfico,
  linha de trabalho) e não por tela: a tela compõe as formas que ela de fato
  tem, na ordem em que as tem.

  `motion-reduce:animate-none` em toda peça: a pulsação é desconforto real
  para quem tem sensibilidade a movimento, e o sistema operacional já sabe
  disso — basta respeitar.
*/

const base = "animate-pulse rounded bg-slate-200/80 motion-reduce:animate-none";

export const SkeletonLine = ({ className = "h-3 w-24" }) => (
  <span className={`block ${base} ${className}`} />
);

export const SkeletonCircle = ({ size = 32 }) => (
  <span
    className={`${base} shrink-0 rounded-full`}
    style={{ width: size, height: size }}
  />
);

/** Cabeçalho de página: título + subtítulo + ação à direita. */
export const SkeletonHeader = ({ withAction = true }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div className="space-y-2">
      <SkeletonLine className="h-5 w-48" />
      <SkeletonLine className="h-3 w-72" />
    </div>
    {withAction ? <SkeletonLine className="h-9 w-36 rounded-xl" /> : null}
  </div>
);

/**
 * Faixa de indicadores — a mesma forma dos KPIs de Tarefas, Pagamentos,
 * Usuários, Funil e da ficha do cliente.
 */
export const SkeletonKpiStrip = ({ count = 4, withIcon = true }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-col divide-y divide-slate-100 sm:flex-row sm:divide-y-0">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={`kpi-${index}`}
          className="flex flex-1 items-start gap-3 border-slate-200 px-5 py-4 sm:border-r sm:last:border-r-0"
        >
          {withIcon ? <SkeletonLine className="h-9 w-9 rounded-xl" /> : null}
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonLine className="h-2.5 w-20" />
            <SkeletonLine className="h-5 w-24" />
            <SkeletonLine className="h-2.5 w-28" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Tabela com cabeçalho e linhas.
 *
 * As larguras das células variam de propósito: uma grade de retângulos
 * idênticos não se parece com dados, e o olho lê isso como "quebrado" em vez
 * de "carregando".
 */
const LARGURAS = ["w-40", "w-24", "w-20", "w-16", "w-28", "w-20"];

export const SkeletonTable = ({ rows = 6, cols = 5, withAvatar = true }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex gap-6 border-b border-slate-200 bg-slate-50 px-4 py-3">
      {Array.from({ length: cols }).map((_, index) => (
        <SkeletonLine key={`th-${index}`} className="h-2.5 w-16" />
      ))}
    </div>

    <div className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, linha) => (
        <div key={`tr-${linha}`} className="flex items-center gap-6 px-4 py-3">
          {withAvatar ? <SkeletonLine className="h-8 w-8 rounded-lg" /> : null}
          {Array.from({ length: cols }).map((_, coluna) => (
            <SkeletonLine
              key={`td-${linha}-${coluna}`}
              className={`h-3 ${LARGURAS[coluna % LARGURAS.length]}`}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Gráfico: barras de alturas diferentes sobre uma linha de base.
 *
 * As alturas são fixas, não aleatórias: um esqueleto que sorteia alturas muda
 * a cada renderização e pisca durante o carregamento.
 */
const ALTURAS = [40, 62, 48, 78, 56, 88, 44, 70, 52, 66, 38, 74];

export const SkeletonChart = ({
  height = 200,
  bars = 12,
  withHeader = true,
  className = "",
}) => (
  <div
    className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}
  >
    {withHeader ? (
      <div className="space-y-2 border-b border-slate-100 px-4 py-3">
        <SkeletonLine className="h-3.5 w-40" />
        <SkeletonLine className="h-2.5 w-56" />
      </div>
    ) : null}

    <div className="p-4">
      <div
        className="flex items-end gap-2 border-b border-slate-100"
        style={{ height }}
      >
        {ALTURAS.slice(0, bars).map((altura, index) => (
          <span
            key={`bar-${index}`}
            className={`${base} flex-1 rounded-t`}
            style={{ height: `${altura}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        {ALTURAS.slice(0, bars).map((_, index) => (
          <SkeletonLine key={`x-${index}`} className="h-2 flex-1" />
        ))}
      </div>
    </div>
  </div>
);

/** Lista de contas/tarefas — a forma de Hoje, das filas e das tarefas. */
export const SkeletonWorkList = ({ rows = 6, withHeader = true }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    {withHeader ? (
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-4 py-2">
        <SkeletonLine className="h-2.5 w-32" />
        <SkeletonLine className="h-6 w-28 rounded-lg" />
      </div>
    ) : null}

    <div className="divide-y divide-slate-100">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={`work-${index}`} className="flex items-center gap-3 px-4 py-3">
          <SkeletonCircle size={36} />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonLine className="h-3 w-48" />
            <SkeletonLine className="h-2.5 w-64" />
          </div>
          <div className="hidden space-y-1.5 sm:block">
            <SkeletonLine className="h-3 w-16" />
            <SkeletonLine className="h-2 w-12" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Bloco de formulário/painel lateral. */
export const SkeletonPanel = ({ lines = 5 }) => (
  <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <SkeletonLine className="h-3.5 w-32" />
    {Array.from({ length: lines }).map((_, index) => (
      <div key={`panel-${index}`} className="flex items-center justify-between gap-3">
        <SkeletonLine className="h-2.5 w-24" />
        <SkeletonLine className="h-2.5 w-20" />
      </div>
    ))}
  </div>
);
